/**
 * ForumList -- WordPress-style brand+model index.
 *
 * Each parent category is rendered as a <CategorySection /> with its
 * children inside. Parents themselves are non-clickable headers; children
 * navigate to /forums/c/:slug for the threads view.
 *
 * Data source: GET /api/categories?tree=true&counts=true — a single call
 * that returns the nested tree (archived filtered out) with `thread_count`
 * on every node, populated server-side via a $group aggregation. No
 * client-side bucket-and-count needed.
 */
import * as React from "react";
import { Link } from "react-router-dom";
import { Flame, PenSquare } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/Button";
import { CategorySection } from "../../components/forum/CategorySection";

interface ChildNode {
  id: string;
  name: string;
  slug: string;
  parent_id: string | null;
  display_order: number;
  thread_count?: number;
  children: ChildNode[];
}

export default function ForumList() {
  const { user } = useAuth();
  const [tree, setTree] = React.useState<ChildNode[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get<ChildNode[]>("/categories", {
          params: { tree: true, counts: true },
        });
        if (cancelled) return;
        setTree(r.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="mx-auto max-w-4xl px-4 md:px-8 py-8 md:py-12">
      {/* ------- Hero ------- */}
      <section
        data-testid="forum-hero"
        className="relative overflow-hidden rounded-3xl border border-surface-border bg-ink text-white mb-8"
      >
        <div
          aria-hidden
          className="absolute inset-0 opacity-30 bg-[radial-gradient(1200px_400px_at_10%_-20%,#E11D2E_0%,transparent_60%)]"
        />
        <div className="relative px-6 py-10 md:px-10 md:py-14 flex items-start justify-between flex-wrap gap-6">
          <div className="flex-1 min-w-0 max-w-3xl">
            <p className="text-brand uppercase text-xs tracking-[0.2em] font-bold mb-4 inline-flex items-center gap-2">
              <Flame className="h-4 w-4" aria-hidden /> Community forum
            </p>
            <h1 className="font-display text-white text-3xl md:text-4xl lg:text-5xl font-black tracking-tight leading-[1.1] mb-4">
              Ask mechanics.
              <br className="hidden md:inline" /> Share parts. Sell in the thread.
            </h1>
            <p className="text-white/70 text-base md:text-lg max-w-xl">
              Browse by boat brand below — every subforum is a focused thread
              list for that make and year range.
            </p>
          </div>
          {user ? (
            <Link to="/forums/new" data-testid="forum-new-thread">
              <Button
                variant="primary"
                size="lg"
                leadingIcon={<PenSquare className="h-5 w-5" />}
              >
                Start a thread
              </Button>
            </Link>
          ) : (
            <Link to="/login">
              <Button variant="outline" size="lg" className="bg-white">
                Sign in to post
              </Button>
            </Link>
          )}
        </div>
      </section>

      {/* ------- Black "ALL CATEGORIES" bar ------- */}
      <div
        data-testid="forum-allcats-bar"
        className="rounded-t-2xl bg-ink text-white px-5 py-3 mb-4 flex items-center justify-between"
      >
        <span className="font-display font-black tracking-wider uppercase text-sm md:text-base">
          Boating Brands — All Categories
        </span>
        <span className="text-white/60 text-xs font-semibold tracking-wider uppercase">
          {tree.length} brand{tree.length === 1 ? "" : "s"}
        </span>
      </div>

      {/* ------- Sections ------- */}
      {loading && (
        <div className="py-16 text-center text-ink-muted" data-testid="forum-loading">
          Loading categories…
        </div>
      )}

      {!loading && tree.length === 0 && (
        <div
          data-testid="forum-empty"
          className="rounded-2xl border-2 border-dashed border-surface-border bg-white p-10 text-center"
        >
          <p className="text-ink font-semibold mb-1">No categories yet.</p>
          <p className="text-ink-muted text-sm">
            Admins can add brands and year-range subforums from the Categories tab.
          </p>
        </div>
      )}

      {!loading && tree.length > 0 && (
        <div
          data-testid="forum-sections"
          className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6"
        >
          {tree.map((parent) => (
            <CategorySection key={parent.id} parent={parent} />
          ))}
        </div>
      )}
    </div>
  );
}
